import dayjs from "dayjs";
import inboxHelper from "../helpers/inbox";
import manualModeHelper from "../helpers/manualMode";

class InboxStore {
    constructor(apiBaseUrl, participantId, resource, studyId, auth) {
        this.apiBaseUrl = apiBaseUrl;
        this.participantId = participantId;
        this.resource = resource;
        this.studyId = studyId;
        this.auth = auth;

        this.messagesObj = {};
        this.cannedResponses = null;
        this.selectedMessage = null;
        this.aiGeneratedResponse = null;
        this.manualModeTimeoutCounter = 0; // for keeping track of inactivity when in manual mode

        this.meta = {
            lastUpdated: null,
            oldest: null,
            pageSize: 50,
            messageLimit: null
        };
        this.loading = {
            older: false,
            initial: false,
            polling: false,
            send: false,
            suggestResponse: false,
            refreshResponse: false,
        };
        this.imageCache = {};
        this.tickCount = 0;
    }

    authCredentials() {
        if (this.auth.method === 'session') {
            return {
                credentials: "include"
            }
        } else if (this.auth.apiKey.length > 0) {
            return {
                headers: new Headers({
                    'Authorization': 'Bearer ' + this.auth.apiKey,
                })
            }
        }
    }

    setMessageLimit(limit) {
        this.meta.messageLimit = limit;
    }

    async fetchImage(url) {
        if (this.imageCache[url]) {
            return this.imageCache[url];
        }

        const auth = this.authCredentials();
        const requestParams = Object.assign(auth, {
            method: "GET"
        });
        const res = await fetch(url, requestParams);
        const blob = await res.blob();

        this.imageCache[url] = URL.createObjectURL(blob);

        return this.imageCache[url];
    }

    getImageUrl(msgId, imageIndex) {
        return `${this.apiBaseUrl}/api/v2/text_messages/${msgId}/image/${imageIndex}`;
    }

    async enableAutomaticMode() {
      const requestParams = Object.assign(
        this.authCredentials(),
        {
          method: "PATCH",
          body: JSON.stringify([
            {op: "end"}
          ])
        }
      );

      await fetch(`${this.apiBaseUrl}/api/v2/participants/${this.participantId}/messaging_mode_session`, requestParams);

      // reload participant state including messaging mode and conversations
      return this.loadMessages();
    }
  async enableReplyWindowMode(duration_minutes) {
    const requestParams = Object.assign(
      this.authCredentials(),
      {
        method: "POST",
        body: JSON.stringify({
          mode: "ReplyWindow",
          duration_mins: duration_minutes
        })
      }
    );
    await fetch(`${this.apiBaseUrl}/api/v2/participants/${this.participantId}/messaging_mode_session`, requestParams);

    // reload participant state including messaging mode and conversations
    return this.loadMessages();
  }
    async enableManualMode() {
        const requestParams = Object.assign(
            this.authCredentials(),
            {
                method: "POST",
                body: JSON.stringify({
                    mode: "Manual",
                    duration_mins: 2
                })
            }
        );
        await fetch(`${this.apiBaseUrl}/api/v2/participants/${this.participantId}/messaging_mode_session`, requestParams);

        // reload participant state including messaging mode and conversations
        return this.loadMessages();
    }

    async disableManualMode() {
        const requestParams = Object.assign(
            this.authCredentials(),
            {
                method: "PATCH",
                body: JSON.stringify([
                    {op: "end"}
                ])
            }
        );

        await fetch(`${this.apiBaseUrl}/api/v2/participants/${this.participantId}/messaging_mode_session`, requestParams);
        this.manualModeTimeoutCounter = 0; // reset the idle counter
        // reload participant state including messaging mode and conversations
        return this.loadMessages();
    }

    async extendManualMode(minutesToExtend) {
      const requestParams = Object.assign(
        this.authCredentials(),
        {
          method: "PATCH",
          body: JSON.stringify([
            {
              op: "extend",
              value: minutesToExtend
            }
          ])
        }
      );

      await fetch(`${this.apiBaseUrl}/api/v2/participants/${this.participantId}/messaging_mode_session`, requestParams);

      // reload participant state including messaging mode and conversations
      return this.loadMessages();
    }

    async getCannedResponses(params) {
        const auth = this.authCredentials();
        const requestParams = Object.assign(auth, {
            method: "GET"
        });

        const search = new URLSearchParams(Object.assign({
            study_id: this.studyId,
            order_by: 'asc(name)',
            per_page: 100000
        }, params));

        const res = await fetch(`${this.apiBaseUrl}/api/v2/canned_responses?` + search, requestParams);

        if (!res.ok) {
            throw new Error("Unable to get canned responses");
        }

        const cannedResponses = (await res.json()).data;
        // Keep null value if empty
        if (cannedResponses.length === 0) {
           return;
        }

        const innerObject = {};
        for(const response of cannedResponses){
            innerObject[response.name] = response.message;
        }
        this.cannedResponses = { all: innerObject };
    }

    async fetchMessages(params, update = true) {
        const auth = this.authCredentials();
        const requestParams = Object.assign(auth, {
            method: "GET"
        });

        const search = new URLSearchParams(Object.assign({
            study_id: this.studyId,
            participant_id: this.participantId,
            order_by: 'desc(id)',
            per_page: this.meta.messageLimit ?? this.meta.pageSize
        }, params));

        const res = await fetch(`${this.apiBaseUrl}/api/v2/text_messages?` + search, requestParams);

        if (!res.ok) {
            throw new Error("womp");
        }

        const messages = (await res.json()).data;
        return this.pullMessagesFromData(messages, update);
    }



    pullMessagesFromData(messages, update = true) {
        if (update) {
            // Our API call finds items updated after the lastUpdated, so if the lastUpdated matched the updated_at
            // of a text we wouldn't get it. Move the search back by a second to account for that
            this.meta.lastUpdated = dayjs().subtract(1, 'second').format('YYYY-MM-DD HH:mm:ss');
        }

        if (messages.length) {
            const newMessages = messages.reduce((accumulator, text) => {
                if (!this.meta.oldest || text.created_at < this.meta.oldest) {
                    this.meta.oldest = text.created_at;
                }
                return {...accumulator, [text.id]: text};
            }, {});
            this.messagesObj = Object.assign({}, this.messagesObj, newMessages)
        }
    }



    async loadMessages() {
        if (this.loading.initial) {
            return false;
        }
        this.loading.initial = true;
        let pageSize = this.meta.messageLimit ?? this.meta.pageSize;
        await this.loadMessagesViaParticipantApi(`limit(${pageSize})`);
        if (manualModeHelper.isManualModeActive) {
          // load any paused conversations
          await this.loadPausedConversation();
        }
        this.loading.initial = false;
    }



    async poll() {
        if (this.loading.polling) {
            return false;
        }

        this.loading.polling = true;
        await this.loadMessagesViaParticipantApi(`updatedAfter(${this.meta.lastUpdated})`)
        this.loading.polling = false;

        if (manualModeHelper.isManualModeActive) {
          // load any paused conversations
          await this.loadPausedConversation();
          this.tickCount++;

          this.manualModeTimeoutCounter++;
          // in the inbox, we are resetting this timout counter whenever they interact with anything
          // if they aren't interacting then they are idle, and we want to disable manual mode after
          // 10 minutes of idleness. Note: this code assumes the poll is called ever 10 seconds.
          if (this.manualModeTimeoutCounter >= 60) {
            await this.disableManualMode();
            this.tickCount = 0;
          } else if (this.tickCount % 3 === 0) {
            await this.extendManualMode(2);
          }
        } else {
          this.tickCount = 0;
        }
    }

    resetManualModeTimeoutCounter() {
      if (manualModeHelper.isManualModeActive) {
        this.manualModeTimeoutCounter = 0;
      }
    }

    async loadMessagesViaParticipantApi(textMessageCriteria) {
        // use the participants/support_partners endpoint for the initial load and when polling, so we get info on timezone, conversations,
        // and messaging mode
        const auth = this.authCredentials();
        const requestParams = Object.assign(auth, {
            method: "GET"
        });
        const includes = [
            `text_messages:${textMessageCriteria}`,
            'messaging_mode_session',
            'current_conversation',
        ];
        const url = `${this.apiBaseUrl}/api/v2/${this.resource}/${this.participantId}?include=${includes.join(',')}`;
        const res = await fetch(url, requestParams);
        if (!res.ok) {
            throw new Error("womp");
        }
        const ppt = (await res.json()).data;
        inboxHelper.setTimezone(ppt?.time_zone_name ?? "America/New_York");
        manualModeHelper.setCurrentSession(ppt?.messaging_mode_session);
        manualModeHelper.setCurrentConversation(ppt?.current_conversation);
        this.pullMessagesFromData(ppt.text_messages, true);

    }

  async loadPausedConversation() {
    if (!manualModeHelper.isManualModeActive) {
      return null;
    }

    const auth = this.authCredentials();
    const requestParams = Object.assign(auth, {
      method: "GET"
    });

    const search = new URLSearchParams({
      study_id: this.studyId,
      participant_id: this.participantId,
      status: 'Paused',
      order_by: 'desc(id)',
      per_page: 1,
      page: 1
    });

    const res = await fetch(`${this.apiBaseUrl}/api/v2/conversations?` + search, requestParams);

    if (!res.ok) {
      throw new Error("Failed to load conversation data");
    }

    const pausedConvo = (await res.json()).data;
    manualModeHelper.setPausedConversation(pausedConvo.length > 0 ? pausedConvo[0] : null);
  }

  async stopConversation(convoId) {
    const body = [
      {
        op: 'close',
      }
    ];
    const auth = this.authCredentials();
    const requestParams = Object.assign(auth, {
      method: "PATCH",
      body: JSON.stringify(body)
    });

    const res = await fetch(`${this.apiBaseUrl}/api/v2/conversations/${convoId}`, requestParams);
    if (!res.ok) {
      throw new Error("womp");
    }
  }



    async loadOlder() {
        if (this.loading.older) {
            return false;
        }

        const params = {
            created_at: 'beforeOrEqual(' + this.meta.oldest + ')',
        };

        this.loading.older = true;
        await this.fetchMessages(params, false);
        this.loading.older = false;
    }


    async sendMessage(message, imageUrl) {
        if (this.loading.send) {
            return false;
        }
        const body = {
            message_text: message,
            media_url: imageUrl,
            force: true
        }

        const auth = this.authCredentials();
        const requestParams = Object.assign(auth, {
            method: "POST",
            body: JSON.stringify(body)
        });

        this.loading.send = true;
        const res = await fetch(`${this.apiBaseUrl}/api/v2/${this.resource}/${this.participantId}/text_messages`, requestParams);
        if (!res.ok) {
            this.loading.send = false;
            if (res.status === 400) {
              const info = await res.json();
              if (info.errors) {
                let errorTxt = "Error sending message: \n\n";
                errorTxt += info.errors.join("\n");
                alert(errorTxt);
              }
              return false;
            }
            throw new Error("womp");
        }
        const text = (await res.json()).data;
        const obj = {};
        obj[text.id] = text;

        this.selectedMessage = null;
        this.messagesObj = Object.assign({}, this.messagesObj, obj);
        this.loading.send = false;
        return true;
    }

    async sendSuggestedMessage(message, imageUrl) {
        if (!this.aiGeneratedResponse) {
            return false;
        }

        const body = [
            {
                op: 'replace',
                path: '/final',
                value: message
            }
        ];
        const auth = this.authCredentials();
        const requestParams = Object.assign(auth, {
            method: "PATCH",
            body: JSON.stringify(body)
        });

        const res = await fetch(`${this.apiBaseUrl}/api/v2/ai_response_requests/${this.aiGeneratedResponse.id}`, requestParams);
        if (!res.ok) {
            this.loading.older = false;
            throw new Error("womp");
        }

        //once we've updated the AiResponseRequest we can generate/send the text message
        await this.sendMessage(message, imageUrl);
        this.aiGeneratedResponse = null;
        this.selectedMessage = null;
    }

    async rejectSuggestedMessage(rejectComment) {
        if (!this.aiGeneratedResponse) {
            return false;
        }

        const body = [
            {
                op: 'replace',
                path: '/review',
                value: 'Rejected'
            },
            {
                op: 'replace',
                path: '/comment',
                value: rejectComment
            },
        ];
        const auth = this.authCredentials();
        const requestParams = Object.assign(auth, {
            method: "PATCH",
            body: JSON.stringify(body)
        });

        const res = await fetch(`${this.apiBaseUrl}/api/v2/ai_response_requests/${this.aiGeneratedResponse.id}`, requestParams);
        if (!res.ok) {
            this.loading.older = false;
            throw new Error("womp");
        }

        this.aiGeneratedResponse = null;
        this.selectedMessage = null;
    }
    async refreshSuggestedMessage() {
        if (this.loading.refreshResponse) {
            return false;
        }

        if (!this.aiGeneratedResponse) {
            return false;
        }

        this.loading.refreshResponse = true;
        const body = [
            {
                op: 'replace',
                path: '/review',
                value: 'Refreshed'
            }
        ];
        const auth = this.authCredentials();
        const requestParams = Object.assign(auth, {
            method: "PATCH",
            body: JSON.stringify(body)
        });

        const res = await fetch(`${this.apiBaseUrl}/api/v2/ai_response_requests/${this.aiGeneratedResponse.id}`, requestParams);
        if (!res.ok) {
            this.loading.older = false;
            throw new Error("womp");
        }

        await this.suggestResponse();
        this.loading.refreshResponse = false;
    }

    async suggestResponse() {
        if (this.loading.suggestResponse) {
            return false;
        }

        if (!this.selectedMessage) {
            return false;
        }

        const body = {
            input: this.selectedMessage.message_text,
            participant_id: this.participantId
        }

        const auth = this.authCredentials();
        const requestParams = Object.assign(auth, {
            method: "POST",
            body: JSON.stringify(body)
        });

        this.loading.suggestResponse = true;
        const res = await fetch(`${this.apiBaseUrl}/api/v2/ai_response_requests`, requestParams);
        if (!res.ok) {
            this.loading.suggestResponse = false;
            throw new Error("womp");
        }
        this.aiGeneratedResponse = (await res.json()).data;
        this.loading.suggestResponse = false;
    }

    setSelectedMessage(message) {
      this.selectedMessage = message;
    }
}

export default InboxStore;
